import React, { useState, useRef } from 'react';
import {
  Box,
  IconButton,
  Avatar,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import useAuth from '../../hooks/useAuth';
import useNotification from '../../hooks/useNotification';

const ProfileImageUpload = ({ size = 80, onClose }) => {
  const { user, uploadProfileImage, deleteProfileImage } = useAuth();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const fileInputRef = useRef(null);
  const menuOpen = Boolean(anchorEl);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('File selected:', file.name, 'type:', file.type, 'size:', file.size);

    // Validate file type
    if (!file.type.match('image/(jpeg|jpg|png|gif)')) {
      console.error('Invalid file type:', file.type);
      showNotification({
        message: 'Please select a valid image file (JPEG, PNG, GIF)',
        type: 'error',
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      console.error('File too large:', file.size);
      showNotification({
        message: 'Image file is too large. Maximum size is 5MB.',
        type: 'error',
      });
      return;
    }

    // Create a preview URL
    const fileUrl = URL.createObjectURL(file);
    setPreviewUrl(fileUrl);
    setSelectedFile(file);
    setOpenPreview(true);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      console.log('No file selected for upload');
      return;
    }

    try {
      setLoading(true);
      console.log('Starting upload for file:', selectedFile.name, 'type:', selectedFile.type, 'size:', selectedFile.size);

      // Additional validation before upload
      if (!(selectedFile instanceof File)) {
        throw new Error('Invalid file object');
      }

      if (!selectedFile.type.startsWith('image/')) {
        throw new Error('Selected file is not an image');
      }

      // Upload the file
      const updatedUser = await uploadProfileImage(selectedFile);
      console.log('Upload successful, updated user:', updatedUser);
      console.log('Current auth context user:', user);
      
      // Force refresh the current component
      setImageError(false);

      showNotification({
        message: 'Profile image uploaded successfully',
        type: 'success',
      });

      // Release object URL to avoid memory leaks
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      setPreviewUrl(null);
      setSelectedFile(null);
      setOpenPreview(false);

      if (onClose) onClose();
    } catch (error) {
      console.error('Error in handleUpload:', error);

      // Detailed error logging
      if (error.response) {
        console.error('Response error:', error.response.status, error.response.data);
      }

      showNotification({
        message: error.message || 'Failed to upload profile image',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setPreviewUrl(null);
    setSelectedFile(null);
    setOpenPreview(false);
  };

  // Menu handlers
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleUploadClick = () => {
    handleMenuClose();
    fileInputRef.current.click();
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      await deleteProfileImage();

      showNotification({
        message: 'Profile image removed successfully',
        type: 'success',
      });

      setDeleteConfirmOpen(false);
      setImageError(false);

      if (onClose) onClose();
    } catch (error) {
      console.error('Error deleting profile image:', error);
      showNotification({
        message: error.message || 'Failed to remove profile image',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = () => {
    // If user has a profile image, show menu with options
    // Otherwise, directly open file picker
    if (user?.profileImage && !imageError) {
      // This will be handled by handleMenuOpen via the IconButton onClick
      return;
    }
    fileInputRef.current.click();
  };

  // For debugging - ensure we have the correct user data
  React.useEffect(() => {
    // console.log('Current user in ProfileImageUpload:', user);
  }, [user]);

  // Handle image load error
  const [imageError, setImageError] = useState(false);

  // Reset image error state if user or profileImage changes
  React.useEffect(() => {
    setImageError(false);
  }, [user?.profileImage]);

  return (
    <>
      <Box sx={{ position: 'relative', width: size, height: size, mx: 'auto', my: 2 }}>
        {user?.profileImage && !imageError ? (
          <Box 
            component="img"
            key={user.profileImage} // Force re-render when image URL changes
            src={user.profileImage}
            alt={user?.name || 'User'}
            onError={() => setImageError(true)}
            sx={{ 
              width: size,
              height: size,
              borderRadius: '50%',
              objectFit: 'cover',
              border: '2px solid',
              borderColor: 'rgba(255,255,255,0.2)',
            }}
          />
        ) : (
          <Avatar
            sx={{
              width: size,
              height: size,
              fontSize: size / 2,
              bgcolor: 'rgba(0,0,0,0.1)',
              border: '2px solid',
              borderColor: 'rgba(255,255,255,0.2)',
            }}
          >
            {user?.name?.charAt(0) || 'U'}
          </Avatar>
        )}
        <IconButton
          onClick={user?.profileImage && !imageError ? handleMenuOpen : triggerFileInput}
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
            width: 32,
            height: 32,
            p: 0.5,
          }}
        >
          <CameraAltIcon fontSize="small" />
        </IconButton>
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </Box>

      {/* Options Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: '5px',
              mr: '5px',
              boxShadow: 'rgba(0, 0, 0, 0.16) 0px 10px 36px 0px, rgba(0, 0, 0, 0.06) 0px 0px 0px 0.5px',
            }
          }
        }}
      >
        <MenuItem onClick={handleUploadClick}>
          <ListItemIcon>
            <CloudUploadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Upload new picture</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Remove picture</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel} maxWidth="xs">
        <DialogTitle>Remove profile picture</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove your profile picture?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={loading}
            startIcon={loading && <CircularProgress size={16} color="inherit" />}
          >
            {loading ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={openPreview} onClose={handleCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Preview Image</DialogTitle>
        <DialogContent>
          {previewUrl && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Avatar
                src={previewUrl}
                alt="Preview"
                sx={{ width: 150, height: 150, mx: 'auto', mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary">
                This image will be used as your profile picture
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading && <CircularProgress size={16} color="inherit" />}
          >
            {loading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProfileImageUpload;